"use client";

import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cookie,
  Shield,
  BarChart3,
  Megaphone,
  Settings,
  X,
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "lucide-react";
import { cn } from "@resume-studio/utils";
import { Button } from "../button";
import { Switch } from "../switch";
import { Card } from "../card";

type CookieManagerProps = {
  domain: "main" | "app";
};

type CookieCategory = {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  required: boolean;
  enabled: boolean;
  cookies?: string[];
};

type ConsentState = {
  necessary: boolean;
  analytics: boolean;
  advertising: boolean;
  functional: boolean;
  timestamp?: number;
};

const CONSENT_KEY = "resumestudio_cookie_consent_v2";
const CONSENT_EXPIRY_DAYS = 365;

// Google Consent Mode mapping
const GOOGLE_CONSENT_MAPPING = {
  analytics: ["analytics_storage"],
  advertising: ["ad_storage", "ad_user_data", "ad_personalization"],
};

export const CookieManager: React.FC<CookieManagerProps> = ({ domain }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [categories, setCategories] = useState<CookieCategory[]>([
    {
      id: "necessary",
      name: "Essential Cookies",
      description: "Required for the website to function properly. These cannot be disabled.",
      icon: <Shield className="size-5" />,
      required: true,
      enabled: true,
      cookies: ["session_id", "csrf_token", "auth_token"],
    },
    {
      id: "analytics",
      name: "Analytics Cookies",
      description:
        "Help us understand how visitors interact with our website by collecting anonymous information.",
      icon: <BarChart3 className="size-5" />,
      required: false,
      enabled: false,
      cookies: ["_ga", "_gid", "_gat", "gtag"],
    },
    {
      id: "advertising",
      name: "Advertising Cookies",
      description:
        "Used to deliver relevant ads and track ad campaign performance. Required for Google AdSense.",
      icon: <Megaphone className="size-5" />,
      required: false,
      enabled: false,
      cookies: ["_gcl_au", "IDE", "DSID", "id", "fcb"],
    },
    {
      id: "functional",
      name: "Functional Cookies",
      description:
        "Enable enhanced functionality and personalization, such as videos and live chats.",
      icon: <Settings className="size-5" />,
      required: false,
      enabled: false,
      cookies: ["lang_preference", "theme_preference", "timezone"],
    },
  ]);

  // Initialize consent state
  useEffect(() => {
    let storedConsent: string | null = null;
    try {
      storedConsent = localStorage.getItem(CONSENT_KEY);
    } catch (error) {
      console.warn("Failed to access localStorage:", error);
    }

    if (storedConsent) {
      try {
        const consent: ConsentState = JSON.parse(storedConsent);
        const daysSinceConsent = consent.timestamp
          ? (Date.now() - consent.timestamp) / (1000 * 60 * 60 * 24)
          : CONSENT_EXPIRY_DAYS + 1;

        if (daysSinceConsent < CONSENT_EXPIRY_DAYS) {
          // Apply stored preferences
          updateCategoriesFromConsent(consent);
          updateGoogleConsent(consent);
          setShowBanner(false);
        } else {
          // Consent expired, show banner again
          setShowBanner(true);
        }
      } catch (error) {
        console.error("Failed to parse consent:", error);
        setShowBanner(true);
      }
    } else {
      // No consent stored, show banner
      setShowBanner(true);
      // Set default Google Consent Mode (denied)
      updateGoogleConsent({
        necessary: true,
        analytics: false,
        advertising: false,
        functional: false,
      });
    }
  }, []);

  const updateCategoriesFromConsent = (consent: ConsentState) => {
    setCategories((prev) =>
      prev.map((cat) => ({
        ...cat,
        enabled: (consent[cat.id as keyof ConsentState] as boolean) ?? cat.enabled,
      })),
    );
  };

  const updateGoogleConsent = (consent: ConsentState) => {
    // Update Google Consent Mode
    if (typeof window !== "undefined" && (window as any).gtag) {
      const gtagConsent: Record<string, string> = {
        functionality_storage: consent.functional ? "granted" : "denied",
        security_storage: "granted", // Always granted for essential cookies
      };

      // Map our categories to Google's consent types
      Object.entries(GOOGLE_CONSENT_MAPPING).forEach(([category, googleTypes]) => {
        const granted = consent[category as keyof ConsentState] ? "granted" : "denied";
        googleTypes.forEach((type) => {
          gtagConsent[type] = granted;
        });
      });

      (window as any).gtag("consent", "update", gtagConsent);
    }
  };

  const saveConsent = useCallback(
    (acceptAll = false) => {
      const consent: ConsentState = {
        necessary: true,
        analytics: acceptAll || categories.find((c) => c.id === "analytics")?.enabled || false,
        advertising: acceptAll || categories.find((c) => c.id === "advertising")?.enabled || false,
        functional: acceptAll || categories.find((c) => c.id === "functional")?.enabled || false,
        timestamp: Date.now(),
      };

      try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
      } catch (error) {
        console.warn("Failed to save consent to localStorage:", error);
      }
      updateGoogleConsent(consent);

      // Set cookies based on consent
      if (domain === "main") {
        if (consent.advertising) {
          document.cookie = `ads_consent=true; max-age=${60 * 60 * 24 * CONSENT_EXPIRY_DAYS}; path=/; domain=.resumestudio.io; SameSite=Lax`;
        }
        if (consent.analytics) {
          document.cookie = `analytics_consent=true; max-age=${60 * 60 * 24 * CONSENT_EXPIRY_DAYS}; path=/; domain=.resumestudio.io; SameSite=Lax`;
        }
      }

      setShowBanner(false);
      setShowSettings(false);
    },
    [categories, domain],
  );

  const handleAcceptAll = () => {
    setCategories((prev) => prev.map((cat) => ({ ...cat, enabled: true })));
    saveConsent(true);
  };

  const handleAcceptSelected = () => {
    saveConsent(false);
  };

  const handleRejectAll = () => {
    setCategories((prev) =>
      prev.map((cat) => ({
        ...cat,
        enabled: cat.required,
      })),
    );
    saveConsent(false);
  };

  const toggleCategory = (categoryId: string) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId && !cat.required ? { ...cat, enabled: !cat.enabled } : cat,
      ),
    );
  };

  const toggleExpanded = (categoryId: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId],
    );
  };

  return (
    <>
      {/* Cookie Banner */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6"
          >
            <Card className="mx-auto max-w-7xl border-gray-200 bg-white/95 shadow-2xl backdrop-blur-md">
              <div className="p-6 sm:p-8">
                <div className="mb-6 flex items-start gap-4">
                  <div className="rounded-full bg-primary/10 p-3">
                    <Cookie className="size-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-2 text-lg font-semibold text-gray-900">
                      We value your privacy
                    </h3>
                    <p className="text-sm text-gray-600 sm:text-base">
                      We use cookies to enhance your browsing experience, analyze site traffic, and
                      serve personalized content.
                      {domain === "main" &&
                        " Some cookies are required for Google AdSense to display relevant advertisements."}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowBanner(false)}
                    className="rounded-lg p-1 transition-colors hover:bg-gray-100"
                  >
                    <X className="size-5 text-gray-500" />
                  </button>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    onClick={handleRejectAll}
                    variant="outline"
                    className="flex-1 sm:flex-none"
                  >
                    Reject All
                  </Button>
                  <Button
                    onClick={() => setShowSettings(true)}
                    variant="outline"
                    className="flex-1 sm:flex-none"
                  >
                    <Settings className="mr-2 size-4" />
                    Manage Preferences
                  </Button>
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-primary hover:bg-primary/90 sm:flex-none"
                  >
                    Accept All
                  </Button>
                </div>

                <p className="mt-4 text-xs text-gray-500">
                  By clicking "Accept All", you agree to the use of cookies as described in our{" "}
                  <a href="/privacy" className="text-primary hover:underline">
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a href="/terms" className="text-primary hover:underline">
                    Cookie Policy
                  </a>
                  .
                </p>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-b border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-primary/10 p-2">
                      <Cookie className="size-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Cookie Preferences</h2>
                      <p className="text-sm text-gray-600">Manage your cookie settings</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="rounded-lg p-2 transition-colors hover:bg-gray-100"
                  >
                    <X className="size-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="max-h-[60vh] overflow-y-auto p-6">
                <div className="space-y-4">
                  {categories.map((category) => (
                    <Card key={category.id} className="border-gray-200 p-4">
                      <div className="mb-2 flex items-start justify-between">
                        <div className="flex flex-1 items-start gap-3">
                          <div
                            className={cn(
                              "rounded-lg p-2",
                              category.enabled
                                ? "bg-primary/10 text-primary"
                                : "bg-gray-100 text-gray-500",
                            )}
                          >
                            {category.icon}
                          </div>
                          <div className="flex-1">
                            <div className="mb-1 flex items-center gap-2">
                              <h3 className="font-medium text-gray-900">{category.name}</h3>
                              {category.required && (
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                  Always Active
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{category.description}</p>
                          </div>
                        </div>
                        <Switch
                          checked={category.enabled}
                          onCheckedChange={() => toggleCategory(category.id)}
                          disabled={category.required}
                          className="ml-4"
                        />
                      </div>

                      {category.cookies && category.cookies.length > 0 && (
                        <button
                          onClick={() => toggleExpanded(category.id)}
                          className="mt-3 flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          {expandedCategories.includes(category.id) ? (
                            <>
                              <ChevronUp className="size-4" />
                              Hide cookies
                            </>
                          ) : (
                            <>
                              <ChevronDown className="size-4" />
                              Show cookies ({category.cookies.length})
                            </>
                          )}
                        </button>
                      )}

                      <AnimatePresence>
                        {expandedCategories.includes(category.id) && category.cookies && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-3 border-t border-gray-100 pt-3">
                              <div className="flex flex-wrap gap-2">
                                {category.cookies.map((cookie) => (
                                  <span
                                    key={cookie}
                                    className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700"
                                  >
                                    {cookie}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Card>
                  ))}
                </div>

                {/* Additional Information */}
                <div className="mt-6 rounded-lg bg-blue-50 p-4">
                  <h4 className="mb-2 flex items-center gap-2 font-medium text-blue-900">
                    <Shield className="size-4" />
                    About Cookies & Google AdSense
                  </h4>
                  <p className="mb-3 text-sm text-blue-800">
                    {domain === "main"
                      ? "We use Google AdSense to display relevant advertisements. Accepting advertising cookies helps support our free service by allowing personalized ads based on your interests."
                      : "This app uses functional cookies to save your preferences and provide essential features for building your resume."}
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <a
                      href="/privacy"
                      className="flex items-center gap-1 text-blue-700 hover:underline"
                    >
                      Privacy Policy
                      <ExternalLink className="size-3" />
                    </a>
                    <a
                      href="/cookie-policy"
                      className="flex items-center gap-1 text-blue-700 hover:underline"
                    >
                      Cookie Policy
                      <ExternalLink className="size-3" />
                    </a>
                    <a
                      href="https://policies.google.com/technologies/ads"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-700 hover:underline"
                    >
                      Google Ads Policy
                      <ExternalLink className="size-3" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 bg-gray-50 p-6">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    onClick={handleRejectAll}
                    variant="outline"
                    className="flex-1 sm:flex-none"
                  >
                    Reject All
                  </Button>
                  <Button
                    onClick={handleAcceptSelected}
                    variant="outline"
                    className="flex-1 sm:flex-none"
                  >
                    <Check className="mr-2 size-4" />
                    Save Preferences
                  </Button>
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-primary hover:bg-primary/90 sm:flex-none"
                  >
                    Accept All
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieManager;
